import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { HttpStatusCode } from 'axios'

import { Filter, PaginatedMeta, PaginatedOptions, RepositoryTaxLot, Sort } from '@types'

import { PAGE_SIZE } from '@/const/common'

import { getPaginatedRequest } from '@/store/api'

import { useRepositoriesStore } from '../repositories/index'

const defaultOptions = () => ({
  search: '',
  appliedSearch: undefined as string | undefined,
  filter: undefined as Filter | undefined,
  sort: [] as Sort[],
  page_no: 0,
  page_size: PAGE_SIZE,
})

export const useLinkedDataTaxLotsStore = defineStore(
  'linked-data-tax-lots',
  () => {
    // INIT
    const repositoriesStore = useRepositoriesStore()

    const list = ref<RepositoryTaxLot[]>([])

    const meta = ref<PaginatedMeta>()

    const loading = ref(false)

    const initFlag = ref(false)

    const error = ref<'permission' | 'error'>()

    const options = ref(defaultOptions())

    let abortController: AbortController | undefined

    let latestRequest = 0

    const repositoryId = computed(() => repositoriesStore.getCurrentId)

    // ACTIONS

    const fetch = async (params: PaginatedOptions) => {
      cancel()
      const requestedRepository = repositoryId.value
      if (!requestedRepository) return
      const request = latestRequest
      // The central repository switch cancels a few microtasks after the id changes.
      const isStale = () =>
        request !== latestRequest || requestedRepository !== repositoryId.value
      abortController = new AbortController()
      loading.value = true
      initFlag.value = true
      error.value = undefined
      try {
        const result = await getPaginatedRequest<RepositoryTaxLot>(
          'linked_data/tax_lots',
          { ...params, repository_id: requestedRepository },
          abortController.signal,
        )
        if (isStale()) return
        list.value = result.data
        meta.value = result.meta
      } catch (e: any) {
        if (isStale()) return
        list.value = []
        meta.value = undefined
        error.value = e?.statusCode === HttpStatusCode.Forbidden ? 'permission' : 'error'
      } finally {
        if (request === latestRequest) loading.value = false
      }
    }

    const cancel = () => {
      latestRequest++
      abortController?.abort()
      loading.value = false
    }

    const clear = () => {
      cancel()
      list.value = []
      meta.value = undefined
      error.value = undefined
      initFlag.value = false
      options.value = defaultOptions()
    }

    return {
      list,
      meta,
      loading,
      initFlag,
      error,
      options,

      fetch,

      cancel,
      clear,
    }
  },
)
